// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import type { Extension, Patient, Reference } from '@medplum/fhirtypes';

export const HSAT_ORDERED_DATE_EXTENSION_URL = 'https://polarissleep.com/fhir/StructureDefinition/hsat-ordered-date';

export const BILLING_PATHWAY_EXTENSION_URL = 'https://polarissleep.com/fhir/StructureDefinition/billing-pathway';
export const BILLING_PATHWAY_CODE_EXTENSION_URL = `${BILLING_PATHWAY_EXTENSION_URL}/pathway`;
export const BILLING_PATHWAY_EFFECTIVE_DATE_EXTENSION_URL = `${BILLING_PATHWAY_EXTENSION_URL}/effective-date`;
export const BILLING_PATHWAY_REASON_EXTENSION_URL = `${BILLING_PATHWAY_EXTENSION_URL}/reason`;
export const BILLING_PATHWAY_SOURCE_EXTENSION_URL = `${BILLING_PATHWAY_EXTENSION_URL}/source`;
export const BILLING_PATHWAY_ACTOR_EXTENSION_URL = `${BILLING_PATHWAY_EXTENSION_URL}/actor`;
export const BILLING_PATHWAY_PREVIOUS_CODE_EXTENSION_URL = `${BILLING_PATHWAY_EXTENSION_URL}/previous-pathway`;
export const BILLING_PATHWAY_RECORDED_AT_EXTENSION_URL = `${BILLING_PATHWAY_EXTENSION_URL}/recorded-at`;

export const BILLING_PATHWAYS = ['INSURANCE', 'SELF-PAY'] as const;
export type BillingPathway = (typeof BILLING_PATHWAYS)[number];
export type BillingPathwaySource = 'intake' | 'provider' | 'backfill';

export interface BillingPathwayDesignation {
  pathway: BillingPathway;
  effectiveDate: string;
  reason: string;
  source: BillingPathwaySource;
  actor?: Reference;
  previousPathway?: BillingPathway;
  recordedAt: string;
}

const BILLING_PATHWAY_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

function isValidDateOnly(value: string): boolean {
  if (!BILLING_PATHWAY_DATE_PATTERN.test(value)) {
    return false;
  }
  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
}

function getDateOfServiceDate(value: string): string {
  return value.slice(0, 10);
}

export function validateBillingPathwayDesignation(designation: BillingPathwayDesignation): string[] {
  const errors: string[] = [];
  if (!BILLING_PATHWAYS.includes(designation.pathway)) {
    errors.push('pathway must be INSURANCE or SELF-PAY');
  }
  if (!isValidDateOnly(designation.effectiveDate)) {
    errors.push('effectiveDate must be a valid YYYY-MM-DD date');
  }
  if (!designation.reason.trim()) {
    errors.push('reason is required');
  }
  if (!['intake', 'provider', 'backfill'].includes(designation.source)) {
    errors.push('source must be intake, provider, or backfill');
  }
  if (designation.source === 'provider' && !designation.actor?.reference) {
    errors.push('actor is required for provider changes');
  }
  if (designation.previousPathway && !BILLING_PATHWAYS.includes(designation.previousPathway)) {
    errors.push('previousPathway must be INSURANCE or SELF-PAY');
  }
  if (!designation.recordedAt || Number.isNaN(Date.parse(designation.recordedAt))) {
    errors.push('recordedAt must be a valid date-time');
  }
  return errors;
}

export function createBillingPathwayExtension(designation: BillingPathwayDesignation): Extension {
  const errors = validateBillingPathwayDesignation(designation);
  if (errors.length > 0) {
    throw new Error(`Invalid billing pathway designation: ${errors.join('; ')}`);
  }
  return {
    url: BILLING_PATHWAY_EXTENSION_URL,
    extension: [
      { url: BILLING_PATHWAY_CODE_EXTENSION_URL, valueCode: designation.pathway },
      { url: BILLING_PATHWAY_EFFECTIVE_DATE_EXTENSION_URL, valueDate: designation.effectiveDate },
      { url: BILLING_PATHWAY_REASON_EXTENSION_URL, valueString: designation.reason },
      { url: BILLING_PATHWAY_SOURCE_EXTENSION_URL, valueCode: designation.source },
      ...(designation.previousPathway
        ? [{ url: BILLING_PATHWAY_PREVIOUS_CODE_EXTENSION_URL, valueCode: designation.previousPathway }]
        : []),
      ...(designation.actor ? [{ url: BILLING_PATHWAY_ACTOR_EXTENSION_URL, valueReference: designation.actor }] : []),
      { url: BILLING_PATHWAY_RECORDED_AT_EXTENSION_URL, valueDateTime: designation.recordedAt },
    ],
  };
}

export function parseBillingPathwayExtension(extension: Extension): BillingPathwayDesignation | undefined {
  if (extension.url !== BILLING_PATHWAY_EXTENSION_URL) {
    return undefined;
  }
  const children = extension.extension ?? [];
  const designation: BillingPathwayDesignation = {
    pathway: children.find((e) => e.url === BILLING_PATHWAY_CODE_EXTENSION_URL)?.valueCode as BillingPathway,
    effectiveDate: children.find((e) => e.url === BILLING_PATHWAY_EFFECTIVE_DATE_EXTENSION_URL)?.valueDate ?? '',
    reason: children.find((e) => e.url === BILLING_PATHWAY_REASON_EXTENSION_URL)?.valueString ?? '',
    source: children.find((e) => e.url === BILLING_PATHWAY_SOURCE_EXTENSION_URL)?.valueCode as BillingPathwaySource,
    actor: children.find((e) => e.url === BILLING_PATHWAY_ACTOR_EXTENSION_URL)?.valueReference,
    previousPathway: children.find((e) => e.url === BILLING_PATHWAY_PREVIOUS_CODE_EXTENSION_URL)?.valueCode as
      | BillingPathway
      | undefined,
    recordedAt: children.find((e) => e.url === BILLING_PATHWAY_RECORDED_AT_EXTENSION_URL)?.valueDateTime ?? '',
  };
  return validateBillingPathwayDesignation(designation).length === 0 ? designation : undefined;
}

export function getBillingPathwayHistory(patient: Patient): BillingPathwayDesignation[] {
  return (patient.extension ?? [])
    .filter((extension) => extension.url === BILLING_PATHWAY_EXTENSION_URL)
    .map(parseBillingPathwayExtension)
    .filter((designation): designation is BillingPathwayDesignation => designation !== undefined);
}

export function withBillingPathwayDesignation(patient: Patient, designation: BillingPathwayDesignation): Patient {
  return {
    ...patient,
    extension: [...(patient.extension ?? []), createBillingPathwayExtension(designation)],
  };
}

/**
 * Adds a pathway designation while ensuring there is only one active designation
 * for its effective date. The replaced designation remains available in the
 * previous Medplum resource version after the patient is updated.
 */
export function withBillingPathwayDesignationReplacingEffectiveDate(
  patient: Patient,
  designation: BillingPathwayDesignation
): Patient {
  const extension = createBillingPathwayExtension(designation);
  const retainedExtensions = (patient.extension ?? []).filter((existing) => {
    if (existing.url !== BILLING_PATHWAY_EXTENSION_URL) {
      return true;
    }
    const existingDesignation = parseBillingPathwayExtension(existing);
    return existingDesignation?.effectiveDate !== designation.effectiveDate;
  });
  return {
    ...patient,
    extension: [...retainedExtensions, extension],
  };
}

export function resolveBillingPathway(patient: Patient, dateOfService: string): BillingPathway | undefined {
  const serviceDate = getDateOfServiceDate(dateOfService);
  if (!isValidDateOnly(serviceDate)) {
    throw new Error('dateOfService must start with a valid YYYY-MM-DD date');
  }
  const applicable = getBillingPathwayHistory(patient)
    .filter((designation) => designation.effectiveDate <= serviceDate)
    .sort((left, right) => right.effectiveDate.localeCompare(left.effectiveDate));
  if (applicable.length > 1 && applicable[0].effectiveDate === applicable[1].effectiveDate) {
    throw new Error(`Ambiguous billing pathway history for effective date ${applicable[0].effectiveDate}`);
  }
  return applicable[0]?.pathway;
}

/** Mirrors the dated pathway into the legacy self-pay tag for a specific effective date. */
export function withBillingPathwayCompatibilityTag(patient: Patient, dateOfService: string): Patient {
  const pathway = resolveBillingPathway(patient, dateOfService);
  const withoutSelfPay = {
    ...patient,
    meta: {
      ...patient.meta,
      tag: (patient.meta?.tag ?? []).filter(
        (tag) => !(tag.system === SELF_PAY_TAG_SYSTEM && tag.code === SELF_PAY_TAG_CODE)
      ),
    },
  };
  return pathway === 'SELF-PAY' ? withSelfPayTag(withoutSelfPay) : withoutSelfPay;
}

/** Tag system and code marking a patient as self-pay (pays out of pocket; insurance is never billed). */
export const SELF_PAY_TAG_SYSTEM = 'http://polarissleep.com/fhir/tags';
export const SELF_PAY_TAG_CODE = 'self-pay';

/** True when the patient carries the self-pay meta tag. */
export function isSelfPayPatient(patient: Patient | undefined): boolean {
  return patient?.meta?.tag?.some((t) => t.system === SELF_PAY_TAG_SYSTEM && t.code === SELF_PAY_TAG_CODE) ?? false;
}

/**
 * Returns a copy of the patient with the self-pay meta tag set.
 * Idempotent: never duplicates the tag. Does not mutate the input.
 */
export const PATIENT_TIMEZONE_EXTENSION_URL = 'http://polarissleep.com/patient-timezone';
export const DEFAULT_PATIENT_TIMEZONE = 'America/New_York';

export function getPatientTimezone(patient: Patient): string {
  return (
    patient.extension?.find((e) => e.url === PATIENT_TIMEZONE_EXTENSION_URL)?.valueString ?? DEFAULT_PATIENT_TIMEZONE
  );
}

export function setPatientTimezone(patient: Patient, timezone: string): Patient {
  const others = (patient.extension ?? []).filter((e) => e.url !== PATIENT_TIMEZONE_EXTENSION_URL);
  return {
    ...patient,
    extension: [...others, { url: PATIENT_TIMEZONE_EXTENSION_URL, valueString: timezone }],
  };
}

export function withSelfPayTag(patient: Patient): Patient {
  const others = (patient.meta?.tag ?? []).filter(
    (t) => !(t.system === SELF_PAY_TAG_SYSTEM && t.code === SELF_PAY_TAG_CODE)
  );
  return {
    ...patient,
    meta: {
      ...patient.meta,
      tag: [...others, { system: SELF_PAY_TAG_SYSTEM, code: SELF_PAY_TAG_CODE }],
    },
  };
}

/** Tag code marking an insurance patient enrolled in the flat-copay bundle pilot. Reuses SELF_PAY_TAG_SYSTEM. */
export const INSURED_COPAY_TAG_CODE = 'insured-copay';

/** True when the patient carries the insured-copay pilot meta tag. */
export function isInsuredCopayPatient(patient: Patient | undefined): boolean {
  return (
    patient?.meta?.tag?.some((t) => t.system === SELF_PAY_TAG_SYSTEM && t.code === INSURED_COPAY_TAG_CODE) ?? false
  );
}

/**
 * Returns a copy of the patient with the insured-copay meta tag set.
 * Idempotent: never duplicates the tag. Does not mutate the input.
 */
export function withInsuredCopayTag(patient: Patient): Patient {
  const others = (patient.meta?.tag ?? []).filter(
    (t) => !(t.system === SELF_PAY_TAG_SYSTEM && t.code === INSURED_COPAY_TAG_CODE)
  );
  return {
    ...patient,
    meta: {
      ...patient.meta,
      tag: [...others, { system: SELF_PAY_TAG_SYSTEM, code: INSURED_COPAY_TAG_CODE }],
    },
  };
}

/** Tag code marking a patient as a test/demo patient. Reuses SELF_PAY_TAG_SYSTEM. */
export const TEST_PATIENT_TAG_CODE = 'test-patient';

/** True when the patient carries the test-patient meta tag. */
export function isTestPatient(patient: Patient | undefined): boolean {
  return patient?.meta?.tag?.some((t) => t.system === SELF_PAY_TAG_SYSTEM && t.code === TEST_PATIENT_TAG_CODE) ?? false;
}

/**
 * Returns a copy of the patient with the test-patient meta tag added (value=true)
 * or removed (value=false). Idempotent. Does not mutate the input.
 */
export function setTestPatient(patient: Patient, value: boolean): Patient {
  const others = (patient.meta?.tag ?? []).filter(
    (t) => !(t.system === SELF_PAY_TAG_SYSTEM && t.code === TEST_PATIENT_TAG_CODE)
  );
  return {
    ...patient,
    meta: {
      ...patient.meta,
      tag: value ? [...others, { system: SELF_PAY_TAG_SYSTEM, code: TEST_PATIENT_TAG_CODE }] : others,
    },
  };
}
